import { Link, getRouteApi } from '@tanstack/react-router';
import { MapPin, Pencil, ShieldCheck, Truck, Wrench } from 'lucide-react';
import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';

import { BadgeStatus } from '@/componentes/BadgeStatus';
import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { EtiquetaDaOpcao } from '@/componentes/EtiquetaDaOpcao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ContextoDoPainel } from '@/componentes/contextoDoPainel';
import { CAMINHO_DO_LOGIN } from '@/componentes/Casca';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { useModoDaCelula } from '@/componentes/celula';
import { HistoriaNaMesa } from '@/historias/mesa';
import { STATUS_EQUIPAMENTO, ehStatusEquipamento, type StatusEquipamento } from '@/lib/dominio';
import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { ehSemPermissao, fraseDoSemPermissao } from '@/lib/perfil-da-licenca';
import { useAbaixoDe } from '@/lib/largura';
import { combinaComTermo, primeiraMaiuscula } from '@/lib/texto';
import { textoDaUltimaLeitura } from '@/modulos/combustivel/ultima-leitura';
import { CustoPorHoraDaMaquina } from '@/modulos/custos/CustoPorHoraDaMaquina';
import type { EstadoDoVencimento, Gatilho } from '@/modulos/manutencao/api';
import { corDoEstado, ROTULO_DO_ESTADO, textoDoFaltam } from '@/modulos/manutencao/vencimento';
import { useBannerDeApontamento } from '@/modulos/painel/banner';
import { useSessao } from '@/modulos/sessao/consultas';

import { descreverAcao } from './trilha';
import { ErroDaApi } from './api';
import { CAMPOS_DA_BUSCA, EXEMPLO_DA_BUSCA, OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, montarBusca } from './busca';
import { useDesativarEquipamento, useFrota, useMaquinasARevisar, useProntuario, useReativarEquipamento } from './consultas-frota';
import { abasDoDetalhe, oQueODetalheLe } from './leitura-do-detalhe';
import { FormularioAlocacao } from './FormularioAlocacao';
import { FormularioFicha } from './FormularioFicha';
import { FormularioSituacao } from './FormularioSituacao';
import { FotoDoEquipamento } from './FotoDoEquipamento';
import { MensagemDeErro } from './MensagemDeErro';
import { MiniaturaDoEquipamento } from './MiniaturaDoEquipamento';
import { useBannerDaRevisao } from './banner-da-revisao';
import { ListaDaRevisao } from './RevisaoDosTipos';
import { FormularioFaixa } from './FormularioFaixa';
import { BlocosDeCustoEPecas } from './CustoEPecasDaFicha';
import { UsoNoProntuario } from './UsoNoProntuario';
import { EconomiaDaMaquina } from '@/modulos/renovacao/EconomiaDaMaquina';
import {
  rotuloDoCombustivel,
  textoDaFaixaDoManual,
  type EquipamentoDaFrota,
  type OrigemDoValor,
  type Prontuario,
} from './frota';

const rota = getRouteApi('/equipamentos');

const ehEstado = (e: string): e is EstadoDoVencimento => Object.hasOwn(ROTULO_DO_ESTADO, e);
const ehGatilho = (g: string): g is Gatilho => g === 'horas' || g === 'km' || g === 'dias';

/**
 * A frota, lendo o BANCO.
 *
 * Esta tela nasceu sobre fixture e a forma dela foi preservada de proposito —
 * as colunas, os quatro indicadores, o painel de detalhe e o estado da busca na
 * URL continuam iguais. O que mudou foi a FONTE, e o que a mudanca de fonte
 * trouxe de novo esta em tres lugares:
 *
 *  - `Horímetro` SAIU da tabela: e leitura de medidor (modulo 03) e mora no
 *    prontuario. `Custo/h` e `Próx. manutenção` VOLTARAM em 22/09, pela
 *    decisao 16 do dono ("ficar como coluna vazia, com o modulo que a
 *    alimentara nomeado"): a manutencao ja preenche a dela; o custo diz que e
 *    do modulo 11. Cliente e obra viraram uma coluna so para caber nas oito.
 *  - `Configuração` e `Tanque` entraram, porque sao o que a ferramenta 1
 *    produz: a versao exata da maquina e o que ela herda dela. A ORIGEM do
 *    valor herdado aparece no painel de detalhe, escrita.
 *  - `Prioridade` saiu do painel. Ela era uma escala inventada pela tela, sem
 *    entrada no manual e sem campo no banco — e agora que ha banco, o que nao
 *    esta nele nao aparece.
 *
 * SEIS status, nunca sete. Uma maquina sem situacao registrada aparece como
 * "Sem situação", que e a ausencia de registro e nao um status novo.
 *
 * ## O orcamento de largura (R01, p.28; p.18)
 *
 * Em L (1280 px) a lista tem 968 px e a R01 pede todas as colunas nela. Com
 * sete colunas e 32 px de respiro em cada uma, nao ha largura para todo texto
 * numa linha so. Quatro decisoes fecham a conta:
 *
 *  - A CONFIGURACAO VIROU A SEGUNDA LINHA DE `Equipamento`, ao lado de
 *    fabricante e modelo — e e a mesma pergunta ("que maquina e esta?"), e a
 *    p.18 manda a segunda linha da celula trazer marca e modelo. Ela continua
 *    visivel; so deixou de custar uma coluna.
 *  - A LINHA PRINCIPAL NUNCA E CORTADA (`Principal`): o tipo da maquina e a
 *    obra sao a identidade legivel da linha. Ela entra na conta de largura da
 *    tabela — e a sobra da tela vai para ela, e nao para o codigo ou o tanque
 *    — e, quando a tela aperta, desce a duas linhas (p.18: nome e texto longo
 *    podem; codigo e numero nao quebram).
 *  - SO A LINHA DE APOIO LEVA RETICENCIAS (`UmaLinha`): marca e modelo,
 *    cliente, motivo, a tarefa da manutencao. O texto inteiro fica no `title`
 *    e no painel de detalhe.
 *  - QUANDO NEM ASSIM CABE, SAI UMA COLUNA SECUNDARIA, e quem decide e o
 *    transbordo medido, nao um numero fixo: ver `useSecundariasOcultas`.
 */
/** A maquina desativada: fora das listas de escolha (alocacao, abastecimento, posto parceiro). */
const ETIQUETA_DESATIVADA = { texto: 'DESATIVADA', tom: 'neutro', titulo: 'Situação do cadastro' } as const;

const COLUNAS: readonly Coluna<EquipamentoDaFrota>[] = [
  {
    chave: 'codigo',
    rotulo: 'Código',
    /* Monoespacada: e o que permite comparar ESC-001 e ESC-002 de relance
       numa coluna de cinquenta linhas (p.18). E NUNCA QUEBRA: "CAM-" em cima
       de "001" desfaz justamente a comparacao vertical. */
    /* A DESATIVADA SE DIZ (31/10/2026): ela some das listas de escolha, e a
       lista da frota a mostrava igual as outras. */
    celula: (e) =>
      e.ativo ? (
        <span className="codigo whitespace-nowrap text-tinta">{e.codigo}</span>
      ) : (
        <span className="flex items-center gap-2">
          <span className="codigo whitespace-nowrap text-tinta-secundaria">{e.codigo}</span>
          <EtiquetaDaOpcao etiqueta={ETIQUETA_DESATIVADA} />
        </span>
      ),
  },
  {
    /* O SEGUNDO CÓDIGO (0125, pedido do dono em 26/10/2026), ao lado do
       primeiro e no mesmo desenho: monoespaçada, sem quebrar. Quem procura a
       máquina pelo nome da planilha da operação a acha na mesma varredura
       vertical. Vazio é o travessão, e não o branco: em coluna de cinquenta
       linhas, célula vazia se lê como falha de carregamento. */
    chave: 'codigoInterno',
    rotulo: 'Código interno',
    celula: (e) =>
      e.codigoInterno === null ? (
        <span className="text-tinta-apagada">—</span>
      ) : (
        <span className="codigo whitespace-nowrap text-tinta-secundaria">{e.codigoInterno}</span>
      ),
  },
  {
    chave: 'equipamento',
    rotulo: 'Equipamento',
    celula: (e) => (
      <span className="flex items-center gap-2">
        {/* A FOTO DA PROPRIA MAQUINA, em recorte quadrado de 32 px, ou o
            `truck` quando ela nao tem foto (p.14: "foto real do proprio ativo
            em recorte quadrado de 32 px, ou o icone Lucide do tipo"). Mora
            AQUI, e nao na coluna Codigo: a foto substitui o icone no lugar
            dele, e o codigo continua a identidade monoespacada que nao some
            ao rolar (p.18). `truck` e a entrada de "equipamento / ativo" no
            vocabulario normativo — a unica que existe para maquina. */}
        <MiniaturaDoEquipamento foto={e.foto} />
        <span className="flex min-w-0 flex-1 flex-col">
          <Principal texto={e.tipo ?? e.descricao} classe="txt-corpo text-tinta" />
          <UmaLinha texto={modeloDaLinha(e)} classe="txt-apoio text-tinta-apagada" />
        </span>
      </span>
    ),
  },
  {
    chave: 'alocacao',
    /* CLIENTE E OBRA NUMA COLUNA SO, em duas linhas. Eram duas, e a decisao 16
       do dono trouxe de volta "Prox. manutencao" e "Custo/h": com as duas
       separadas a lista passaria das oito colunas que o manual permite (p.18).
       Obra em cima, porque e onde a maquina esta; cliente embaixo, em apoio.
       Cliente e o do SERVICO — a construtora —, e nao o locatario do sistema
       do ADR-002, que nunca aparece em tela. */
    rotulo: 'Obra · cliente',
    celula: (e) =>
      e.obra ? (
        <span className="flex flex-col">
          {/* O pino acompanha a PRIMEIRA linha da obra, e nao o meio das
              duas, quando ela desce a duas linhas. */}
          <span className="flex items-start gap-2">
            <span className="flex h-5 shrink-0 items-center">
              <MapPin className="size-[var(--icone-controle)]" />
            </span>
            <Principal texto={e.obra} classe="txt-corpo text-tinta" />
          </span>
          <UmaLinha texto={e.cliente ?? 'Sem cliente'} classe="txt-apoio text-tinta-apagada" />
        </span>
      ) : (
        <span className="whitespace-nowrap text-tinta-secundaria">Sem alocação</span>
      ),
  },
  {
    chave: 'status',
    rotulo: 'Status',
    historia: 'SEL-EQP-01',
    alinhamento: 'status',
    /* O MOTIVO E ESCRITO, e nao so `title`: "Aguardando peça — bomba
       hidráulica" e a primeira pergunta de quem ve "Parada", e num atributo de
       hover ele nao chega por teclado nem por leitor de tela. Ele ocupa a
       largura que a coluna tiver e corta ali — com `whitespace-nowrap` o motivo
       mais longo da pagina fixava a largura da coluna e espremia as outras. O
       motivo inteiro esta no historico da aba Situacao. */
    celula: (e) => (
      /* O selo nunca quebra: "Em" em cima de "deslocamento" nao e mais um selo. */
      <span className="flex flex-col items-start gap-1 whitespace-nowrap">
        {!e.ativo ? (
          <EtiquetaDaOpcao etiqueta={ETIQUETA_DESATIVADA} />
        ) : e.status ? (
          <BadgeStatus status={e.status} />
        ) : (
          <span className="txt-apoio text-tinta-secundaria">Sem situação</span>
        )}
        {e.ativo && e.motivoDoStatus ? (
          <UmaLinha texto={e.motivoDoStatus} classe="txt-apoio text-tinta-apagada" />
        ) : null}
      </span>
    ),
  },
  {
    chave: 'tanque',
    rotulo: 'Tanque',
    alinhamento: 'numero',
    celula: (e) =>
      e.capacidadeTanqueL === null ? (
        <span className="text-tinta-secundaria">—</span>
      ) : (
        <Medida valor={formatarInteiro(e.capacidadeTanqueL)} unidade="L" />
      ),
  },
  {
    chave: 'manutencao',
    /* DECISAO 16 DO DONO: a coluna volta. A tarefa mais urgente da fila do
       modulo 05, com a frase da propria fila ("faltam 35,0 h"). Sem a
       permissao do modulo, a celula diz isso — e nao "sem plano". */
    rotulo: 'Próx. manutenção',
    celula: (e) => {
      const p = e.proximaManutencao;
      if (!p.permitido) {
        return <Principal texto="Sem acesso à manutenção" classe="txt-apoio text-tinta-secundaria" />;
      }
      if (!p.item) {
        return <Principal texto="Sem plano preventivo" classe="txt-apoio text-tinta-secundaria" />;
      }
      const faltam =
        ehGatilho(p.item.gatilho) ? textoDoFaltam({ faltam: p.item.faltam, gatilho: p.item.gatilho }) : null;
      return (
        <span className="flex flex-col">
          {/* R02: vencida em vermelho, próxima em âmbar. */}
          <Principal
            texto={ehEstado(p.item.estado) ? ROTULO_DO_ESTADO[p.item.estado] : p.item.estado}
            classe={`txt-corpo ${corDoEstado(p.item.estado) ?? 'text-tinta'}`}
          />
          <UmaLinha
            texto={[p.item.descricao, faltam].filter(Boolean).join(' · ')}
            classe="txt-apoio text-tinta-apagada"
          />
        </span>
      );
    },
  },
  {
    chave: 'custo',
    /* DECISAO 16 DO DONO: a coluna voltou vazia, dizendo quem a preencheria.
       O modulo 11 existe desde 22/09, e ela passa a mostrar o custo direto por
       hora de motor dos ultimos 30 dias — escrito como parcial quando e.
       Sem quebra: "sem hora apontada" em duas linhas passava dos 48 px. */
    rotulo: 'Custo/h',
    historia: 'NUM-EQP-08',
    alinhamento: 'numero',
    celula: (e) => (
      <span className="block whitespace-nowrap">
        <CustoPorHoraDaMaquina equipamentoId={e.id} />
      </span>
    ),
  },
];

/**
 * A linha PRINCIPAL da celula: o tipo da maquina, a obra, o estado da
 * manutencao. Nunca leva reticencias numa largura de trabalho.
 *
 * Segue o `TextoDaCelula` da tabela: entra na conta de largura com o texto
 * inteiro — a sobra da tela vai para ela — e, quando a tela aperta, desce a
 * duas linhas, quebrando entre palavras. O que esta tela acrescenta:
 *
 *  - O PISO DE 96 PX (`min-w-24`), que segura um nome de obra comprido em duas
 *    linhas, e nao em quatro. Sem ele a coluna encolheria ate a palavra mais
 *    longa e a tabela nunca passaria do quadro — e passar do quadro e o sinal
 *    para tirar uma coluna secundaria (`useSecundariasOcultas`).
 *  - A PALAVRA COM HIFEN INTEIRA: "Duplicação BR-" / "153 km 12" partia a
 *    rodovia no meio. `BR-153` fica numa linha so.
 *  - A TERCEIRA LINHA ANTES DO CORTE: no aperto extremo (texto em caixa alta,
 *    lista de 768 px com o painel aberto), o nome vai a tres linhas em vez de
 *    ser cortado. Reticencias so alem disso, com o texto no `title` e no
 *    painel.
 *
 * No cartao (abaixo de 768 px) nao corta nunca, como o `TextoDaCelula`.
 */
function Principal({ texto, classe }: { readonly texto: string; readonly classe: string }) {
  const modo = useModoDaCelula();
  const partes = texto
    .split(/(\S+-\S+)/)
    .map((parte, indice) =>
      indice % 2 === 1 ? (
        <span key={indice} className="whitespace-nowrap">
          {parte}
        </span>
      ) : (
        parte
      ),
    );
  if (modo === 'cartao') {
    return <span className={`block text-pretty [overflow-wrap:anywhere] ${classe}`}>{partes}</span>;
  }
  return (
    <span className={`line-clamp-3 min-w-24 max-w-[40ch] whitespace-normal text-pretty ${classe}`} title={texto}>
      {partes}
    </span>
  );
}

/**
 * A linha de APOIO (p.18: segunda linha em 12 px, apagada): ocupa a largura
 * que a coluna TIVER e corta ali, com reticencias. `w-0` tira o texto da conta
 * de largura da tabela — quem disputa a largura e a linha principal; se o
 * apoio entrasse na conta, a 1440 ele tomaria a largura do tipo da maquina e o
 * faria quebrar —, e `min-w-full` o estica ate a largura que a coluna acabou
 * recebendo. O texto inteiro vai no `title`, e o painel de detalhe o mostra
 * sem corte. No cartao (abaixo de 768 px) a tabela desfaz o `truncate` e o
 * apoio quebra linha.
 */
function UmaLinha({ texto, classe }: { readonly texto: string; readonly classe: string }) {
  return (
    <span className={`block w-0 min-w-full truncate ${classe}`} title={texto}>
      {texto}
    </span>
  );
}

/**
 * Fabricante e modelo, e a configuracao ao lado — sem repetir o modelo. O
 * nome da configuracao costuma comecar por ele ("FMX 440 — caçamba 12 m³"), e
 * "Volvo FMX 440 · FMX 440 — caçamba 12 m³" gastava a linha dizendo duas
 * vezes a mesma coisa. O nome inteiro continua na ficha.
 */
function modeloDaLinha(e: EquipamentoDaFrota): string {
  const modelo = [e.fabricante, e.modelo].filter(Boolean).join(' ');
  return [modelo, configuracaoSemOModelo(e.configuracao, e.modelo)].filter(Boolean).join(' · ') || 'Sem modelo cadastrado';
}

function configuracaoSemOModelo(configuracao: string | null, modelo: string | null): string | null {
  if (!configuracao || !modelo) return configuracao;
  const inicio = configuracao.slice(0, modelo.length);
  const resto = configuracao.slice(modelo.length);
  /* So o modelo INTEIRO seguido de separador: "320D" nao e prefixo de "320". */
  if (inicio.toLocaleLowerCase('pt-BR') !== modelo.toLocaleLowerCase('pt-BR') || !/^([\s—–·:,-]|$)/.test(resto)) {
    return configuracao;
  }
  return resto.replace(/^[\s—–·:,-]+/, '') || null;
}

/**
 * R01 (p.28): L e XL mostram "todas as colunas"; M (abaixo de 1280 px)
 * "oculta colunas secundarias"; S fica com as "colunas-chave" e rola o quadro.
 * Secundarias, na ordem em que saem: o tanque, que e dado de ficha e esta no
 * painel; depois o custo, que tem tela propria. Codigo, maquina, obra, status
 * e manutencao sao a pergunta do dia e nao saem nunca.
 */
const SECUNDARIAS: readonly string[] = ['tanque', 'custo'];

/**
 * Quantas colunas secundarias saem, pelo TRANSBORDO MEDIDO.
 *
 * Os limites fixos de antes (952 e 868 px de lista) eram a soma das larguras
 * minimas do texto de HOJE: com o texto 15% mais largo — caixa alta, outro
 * nome de obra —, a lista de 1280 px passava do quadro e o `Custo/h` so
 * aparecia rolando de lado. Agora a tabela e desenhada com todas as colunas
 * (em L e XL) e, se ela passar do quadro, sai a proxima secundaria, ate nao
 * passar mais. Abaixo de 1280 px as duas ja comecam fora (R01, M).
 *
 * Conta pela LISTA, e nao pela janela: com o painel de detalhe fixo ao lado,
 * a lista de uma tela de 1440 px tem os mesmos 768 px da faixa S. A medida
 * recomeca quando a lista muda de largura ou de linhas; e a tabela e observada
 * tambem, porque a celula de custo e a fonte chegam depois do desenho.
 */
function useSecundariasOcultas<T extends HTMLElement>(linhasNaTela: string) {
  const alvo = useRef<T>(null);
  const [largura, setLargura] = useState<number | null>(null);
  const minimo = useAbaixoDe(1280) ? SECUNDARIAS.length : 0;
  const chave = `${String(largura)}|${linhasNaTela}|${String(minimo)}`;
  const [medida, setMedida] = useState({ chave: '', fora: 0 });
  const ocultas = Math.max(minimo, medida.chave === chave ? medida.fora : 0);

  useEffect(() => {
    const no = alvo.current;
    if (!no || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(([entrada]) => {
      if (entrada) setLargura(Math.round(entrada.contentRect.width));
    });
    observador.observe(no);
    return () => {
      observador.disconnect();
    };
  }, []);

  /* Sem lista de dependencias: a tabela e outra a cada troca de pagina, de
     filtro ou de colunas, e o observador vai junto. A primeira observacao ja
     dispara a medida. */
  useEffect(() => {
    const tabela = alvo.current?.querySelector('table');
    const quadro = tabela?.parentElement;
    if (!tabela || !quadro || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => {
      if (ocultas >= SECUNDARIAS.length || quadro.scrollWidth <= quadro.clientWidth) return;
      setMedida((agora) => (agora.chave === chave && agora.fora > ocultas ? agora : { chave, fora: ocultas + 1 }));
    });
    observador.observe(tabela);
    return () => {
      observador.disconnect();
    };
  });

  return [alvo, ocultas] as const;
}

/**
 * Como a tela escreve a origem de um valor herdado. O valor AUSENTE nao tem
 * linha de origem: "Não informado" em cima de "não informado" so repetia.
 */
const ORIGEM: Record<Exclude<OrigemDoValor, 'ausente'>, string> = {
  unidade: 'definido nesta máquina',
  configuracao: 'herdado da configuração',
  /* So o `tipo` herda daqui (0124): a configuracao nunca teve tipo. */
  modelo: 'herdado do modelo',
  padrao: 'padrão do sistema',
};

function origemEscrita(origem: OrigemDoValor): string | undefined {
  return origem === 'ausente' ? undefined : ORIGEM[origem];
}

export function PaginaEquipamentos() {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const consulta = useFrota();
  /* Cadastrar, importar e desativar são de quem gere o equipamento (prova do
     marco, 07/10/2026): a frota mostrava os três para a Oficina, que só lê. */
  const sessaoDaLista = useSessao();
  const podeGerirFrota = (sessaoDaLista.data?.permissoes ?? []).includes('equipamento.gerir');
  /* O banner da p.33 também na frota: máquinas operando sem turno apontado. */
  useBannerDeApontamento();

  const [selecionado, setSelecionado] = useState<string | null>(null);

  /* REVISAR TIPOS (0131, decisão 57): as máquinas com tipo fora da lista ou
     código de outro tipo. Lê com a mesma licença da lista da frota. */
  const podeLerFrota = (sessaoDaLista.data?.permissoes ?? []).includes('equipamento.ler');
  const aRevisar = useMaquinasARevisar(podeLerFrota);
  const [revisando, setRevisando] = useState(false);
  const listaDaRevisao = useRef<HTMLElement>(null);
  const maquinasARevisar = aRevisar.data ?? [];
  useBannerDaRevisao(maquinasARevisar.length, () => {
    setRevisando(true);
    /* Depois de desenhar: leva a lista à vista e o foco a ela. */
    window.requestAnimationFrame(() => {
      listaDaRevisao.current?.scrollIntoView({ block: 'start' });
      listaDaRevisao.current?.focus({ preventScroll: true });
    });
  });

  const termo = busca.termo ?? '';
  const status = busca.status;
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  const todos = useMemo(() => consulta.data ?? [], [consulta.data]);

  const filtrados = useMemo(
    () =>
      todos.filter(
        (e) =>
          (status ? e.status === status : true) &&
          /* Os campos aqui sao os mesmos que `CAMPOS_DA_BUSCA` anuncia no
             `title` do campo, na mesma ordem — e a constante e uma so, usada tambem
             pela busca da barra superior, que escreve no mesmo `?termo=`. */
          combinaComTermo(
            [e.codigo, e.codigoInterno, e.tipo, e.modelo, e.fabricante, e.cliente, e.obra],
            termo,
          ),
      ),
    [todos, status, termo],
  );

  /* SO HA INDICADOR QUANDO HA FROTA. Enquanto a lista carrega, ou quando ela
     falha, `todos` e `[]` — e calcular sobre isso punha "Frota 0" e "Operando
     agora 0 de 0" na tela ao lado do esqueleto da tabela: um dado falso com
     cara de dado (regra 9; p.17, o numero precisa significar algo). */
  const indicadores = useMemo(
    () => (consulta.data ? calcularIndicadores(consulta.data) : null),
    [consulta.data],
  );
  /* Enquanto carrega, nada de frase no lugar do dado (p.32): os cards viram
     esqueleto pelo `carregando`, e quem anuncia a frota que vem e a tabela. */
  const semIndicador = consulta.isError ? 'Não foi possível carregar' : '';

  const paginas = Math.max(Math.ceil(filtrados.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = filtrados.slice(inicio, inicio + porPagina);

  const [lista, ocultas] = useSecundariasOcultas<HTMLDivElement>(visiveis.map((e) => e.id).join(','));
  const colunas = useMemo(
    () => COLUNAS.filter((coluna) => !SECUNDARIAS.slice(0, ocultas).includes(coluna.chave)),
    [ocultas],
  );

  const aberto = filtrados.find((e) => e.id === selecionado) ?? null;

  /* A máquina da revisão abre no painel; se o filtro a esconde, ele sai. */
  function abrirDaRevisao(id: string) {
    if (!filtrados.some((e) => e.id === id)) irPara({ termo: '', status: undefined, pagina: 1, porPagina });
    setSelecionado(id);
  }

  function irPara(estado: {
    readonly termo: string;
    readonly status: StatusEquipamento | undefined;
    readonly pagina: number;
    readonly porPagina: number;
    /** `true` para nao encher o historico do navegador a cada tecla. */
    readonly substituir?: boolean;
  }) {
    void navegar({
      search: montarBusca(estado),
      ...(estado.substituir ? { replace: true } : {}),
    });
  }

  /* Trocar filtro sempre volta para a primeira pagina: manter a pagina 3 depois
     de filtrar mostra tela vazia e parece defeito do sistema. */
  const chips: readonly ChipFiltro[] = [
    ...(status
      ? [
          {
            id: 'status',
            rotulo: `Status: ${status}`,
            aoRemover: () => {
              irPara({ termo, status: undefined, pagina: 1, porPagina });
            },
          },
        ]
      : []),
    ...(termo
      ? [
          {
            id: 'termo',
            rotulo: `Busca: ${termo}`,
            aoRemover: () => {
              irPara({ termo: '', status, pagina: 1, porPagina });
            },
          },
        ]
      : []),
  ];

  function limparFiltros() {
    irPara({ termo: '', status: undefined, pagina: 1, porPagina });
  }

  const semSessao = consulta.error instanceof ErroDaApi && consulta.error.status === 401;

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Frota"
            titulo="Equipamentos"
            subtitulo="Situação da frota agora, por obra e por centro de custo"
            acoes={
              <>
                {/* Um nome so para o lugar: "Catálogo" no menu, aqui e no
                    titulo da tela. */}
                <Link to="/equipamentos/catalogo" className="botao botao-secundario">
                  Abrir catálogo
                </Link>
                {/* A IDENTIFICAÇÃO (decisão 64, 31/10/2026): o arquivo dos
                    adesivos com o código e os QR, para a gráfica. Substitui
                    os "Adesivos de leitura" de 26/10. Contorno: o laranja da
                    tela continua sendo o cadastro. */}
                <Link to="/equipamentos/identificacao" className="botao botao-secundario">
                  Identificação
                </Link>
                {/* A frota pela planilha (0060): contorno, porque o laranja da
                    tela continua sendo o cadastro de uma máquina. */}
                {podeGerirFrota ? (
                  <Link to="/equipamentos/importar" className="botao botao-secundario">
                    Importar planilha
                  </Link>
                ) : null}
                {/* A UNICA acao laranja desta tela (regra 1 do checklist). */}
                {podeGerirFrota ? (
                  <HistoriaNaMesa codigo="BTN-EQP-03" className="flex">
                    <Link to="/equipamentos/novo" className="botao botao-primario">
                      Cadastrar equipamento
                    </Link>
                  </HistoriaNaMesa>
                ) : null}
              </>
            }
          />

          {revisando && maquinasARevisar.length > 0 ? (
            <ListaDaRevisao
              ref={listaDaRevisao}
              maquinas={maquinasARevisar}
              podeGerir={podeGerirFrota}
              aoAbrir={abrirDaRevisao}
              aoFechar={() => {
                setRevisando(false);
              }}
            />
          ) : null}

          <FaixaIndicadores>
            <CardIndicador
              rotulo="Frota"
              icone={Truck}
              tom="marca"
              carregando={consulta.isPending}
              valor={indicadores ? formatarInteiro(indicadores.total) : '—'}
              contexto={indicadores ? 'equipamentos cadastrados' : semIndicador}
              aoAbrir={limparFiltros}
            />
            <CardIndicador
              rotulo="Operando agora"
              icone={Truck}
              tom="operando"
              carregando={consulta.isPending}
              valor={indicadores ? formatarInteiro(indicadores.operando) : '—'}
              contexto={indicadores ? `de ${formatarInteiro(indicadores.total)} equipamentos` : semIndicador}
              {...(indicadores && indicadores.total > 0 ? { proporcao: indicadores.operando / indicadores.total } : {})}
              aoAbrir={() => {
                irPara({ termo, status: 'Operando', pagina: 1, porPagina });
              }}
            />
            <CardIndicador
              rotulo="Fora de operação"
              icone={Wrench}
              tom="parada"
              carregando={consulta.isPending}
              /* R02: máquina parada é o próprio alerta. Zero sai em preto. */
              alerta={indicadores ? indicadores.foraDeOperacao > 0 : false}
              {...(indicadores && indicadores.total > 0 ? { proporcao: indicadores.foraDeOperacao / indicadores.total } : {})}
              valor={indicadores ? formatarInteiro(indicadores.foraDeOperacao) : '—'}
              contexto={
                indicadores
                  ? `${formatarInteiro(indicadores.paradas)} parada, ${formatarInteiro(indicadores.manutencao)} em manutenção, de ${formatarInteiro(indicadores.total)}`
                  : semIndicador
              }
            />
            {/* NEUTRO, e nao o azul de `Disponível`: o card conta TODA máquina
                sem obra — parada e em manutenção inclusive —, e o azul diria
                "apta e sem alocação". Sem obra é o "Não alocado" da p.09. */}
            <CardIndicador
              rotulo="Sem alocação"
              icone={MapPin}
              tom="neutro"
              carregando={consulta.isPending}
              {...(indicadores && indicadores.total > 0 ? { proporcao: indicadores.semAlocacao / indicadores.total } : {})}
              valor={indicadores ? formatarInteiro(indicadores.semAlocacao) : '—'}
              contexto={
                indicadores ? `de ${formatarInteiro(indicadores.total)} equipamentos, sem obra aberta` : semIndicador
              }
            />
          </FaixaIndicadores>

          <BarraFiltros
            chips={chips}
            contagem={
              consulta.data
                ? `${formatarInteiro(filtrados.length)} de ${formatarInteiro(todos.length)} equipamentos`
                : consulta.isPending
                  ? null
                  : semIndicador
            }
            aoLimpar={limparFiltros}
          >
            {/* O exemplo curto no campo; os seis campos que a busca olha, no
                `title` — a promessa do campo continua inteira. */}
            <div className="min-w-0 flex-1" title={`Busca por ${CAMPOS_DA_BUSCA.toLocaleLowerCase('pt-BR')}.`}>
              <CampoTexto
                rotulo="Buscar"
                valor={termo}
                placeholder={EXEMPLO_DA_BUSCA}
                atalho="ctrl-k"
                aplicado={Boolean(termo)}
                aoMudar={(novo) => {
                  irPara({ termo: novo, status, pagina: 1, porPagina, substituir: true });
                }}
              />
            </div>

            <CampoSelecao
              rotulo="Status"
              valor={status ?? ''}
              rotuloVazio="Todos"
              aplicado={Boolean(status)}
              opcoes={STATUS_EQUIPAMENTO.map((nome) => ({ valor: nome, rotulo: nome }))}
              aoMudar={(novo) => {
                irPara({
                  termo,
                  status: ehStatusEquipamento(novo) ? novo : undefined,
                  pagina: 1,
                  porPagina,
                });
              }}
            />
          </BarraFiltros>

          {/* A lista e a origem do painel: clicar em outra linha troca o detalhe
              em vez de fecha-lo. */}
          <div ref={lista} data-painel-origem>
            <Tabela
              rotulo="Equipamentos da frota"
              colunas={colunas}
              linhas={visiveis}
              alturaDupla
              chaveDaLinha={(e) => e.id}
              carregando={consulta.isPending}
              selecionada={selecionado}
              aoSelecionar={(e) => {
                setSelecionado(e.id);
              }}
              erro={
                consulta.isError ? (
                  semSessao ? (
                    /* 401 nao e falha de carregamento, e ausencia de sessao — e
                       mandar a pessoa "tentar de novo" faria ela procurar
                       problema no lugar errado. */
                    <EstadoVazio
                      titulo="Sua sessão não está aberta"
                      descricao="Sua sessão não está aberta, ou expirou. Entre outra vez para ver a frota."
                      acao={
                        <Link to={CAMINHO_DO_LOGIN} className="botao botao-secundario">
                          Entrar de novo
                        </Link>
                      }
                    />
                  ) : (
                    <EstadoVazio
                      titulo="Não foi possível carregar a frota"
                      descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                      acao={
                        <Botao
                          nivel="secundario"
                          onClick={() => {
                            void consulta.refetch();
                          }}
                        >
                          Carregar a frota
                        </Botao>
                      }
                    />
                  )
                ) : null
              }
              vazio={
                todos.length === 0 ? (
                  /* Primeiro uso: a lista esta vazia porque ninguem cadastrou
                     nada ainda. Texto diferente do "filtro nao achou", de
                     proposito — sao dois problemas com duas saidas. */
                  <EstadoVazio
                    titulo="Nenhum equipamento cadastrado"
                    descricao={
                      podeGerirFrota
                        ? 'Cadastre o primeiro equipamento para começar a acompanhar a frota.'
                        : 'Quem cadastra a frota é o perfil Administrador. As máquinas aparecem aqui depois de cadastradas.'
                    }
                    acao={
                      podeGerirFrota ? (
                        <Link to="/equipamentos/novo" className="botao botao-secundario">
                          Cadastrar equipamento
                        </Link>
                      ) : undefined
                    }
                  />
                ) : (
                  <EstadoVazio
                    titulo="Nenhum equipamento com esses filtros"
                    descricao="Troque o status, apague a busca ou limpe os filtros para ver a frota inteira."
                    acao={
                      <Botao nivel="secundario" onClick={limparFiltros}>
                        Limpar filtros
                      </Botao>
                    }
                  />
                )
              }
              rodape={
                <RodapeTabela
                  primeiro={inicio + 1}
                  ultimo={inicio + visiveis.length}
                  total={filtrados.length}
                  pagina={paginaSegura}
                  paginas={paginas}
                  porPagina={porPagina}
                  opcoesPorPagina={OPCOES_POR_PAGINA}
                  aoMudarPagina={(nova) => {
                    irPara({ termo, status, pagina: nova, porPagina });
                  }}
                  aoMudarPorPagina={(novo) => {
                    irPara({ termo, status, pagina: 1, porPagina: novo });
                  }}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* `key`: trocar de maquina descarta a edicao aberta da anterior. */}
      {aberto ? <Prontuario key={aberto.id} equipamento={aberto} aoFechar={() => { setSelecionado(null); }} /> : null}
    </>
  );
}

/**
 * Ferramenta 2 — o prontuario: a ficha e a linha do tempo do ativo.
 *
 * Quatro abas, que e o teto do manual (p.19). A quarta, `Histórico`, e a trilha
 * de auditoria — a mesma que o trigger do banco escreve, sem copia nenhuma. Se
 * houvesse uma segunda tabela "historico do equipamento", ela divergiria da
 * trilha no primeiro erro parcial.
 */
function Prontuario({
  equipamento,
  aoFechar,
}: {
  readonly equipamento: EquipamentoDaFrota;
  readonly aoFechar: () => void;
}) {
  const sessao = useSessao();
  /* O PAINEL SÓ PEDE O QUE O PERFIL LÊ (robôs, rodada 2): sem `prontuario.ler`
     a consulta não sai, e o uso, o consumo, custo e peças, os históricos e a
     aba Histórico não aparecem — o painel fica com a ficha que a lista já
     trouxe. Antes: 403, toast e "Você não tem acesso" dentro do painel. */
  const le = oQueODetalheLe(sessao.data?.permissoes ?? null);
  const abasVisiveis = abasDoDetalhe(sessao.data?.permissoes ?? null);
  const prontuario = useProntuario(le.prontuario ? equipamento.id : null);
  const desativar = useDesativarEquipamento();
  const reativar = useReativarEquipamento();
  const podeEditar = (sessao.data?.permissoes ?? []).includes('equipamento.gerir');
  const [editando, setEditando] = useState(false);
  const idDaFicha = useId();
  /* O painel em volta, para "Editar ficha" poder expandi-lo. */
  const painel = useContext(ContextoDoPainel);

  const medidor = equipamento.tipoMedidor === 'odometro' ? 'Odômetro' : 'Horímetro';

  return (
    <PainelDetalhe
      key={equipamento.id}
      /* A IDENTIDADE DA P.19: o codigo pequeno, em monoespacada, e o que a
         maquina E como titulo ("ESC-001" sobre "Escavadeira CAT 320"). Com o
         codigo no titulo, o tipo ia para a linha cinza de 13 px, quebrada em
         duas ao lado de "Expandir painel". */
      codigo={equipamento.codigo}
      titulo={equipamento.tipo ?? equipamento.descricao}
      /* A ULTIMA LEITURA NA DESCRICAO DO CABECALHO (decisao 54, 28/10/2026):
         quem abre a maquina para lancar ve o ultimo horimetro ou km sem
         procurar na ficha. */
      subtitulo={[
        [equipamento.fabricante, equipamento.modelo].filter(Boolean).join(' ') || 'Sem modelo cadastrado',
        textoDaUltimaLeitura(
          equipamento.medidorAtual ? { valor: equipamento.medidorAtual.valor, em: equipamento.medidorAtual.lidaEm, doAparelho: false } : null,
          equipamento.tipoMedidor === 'odometro' ? 'km' : 'h',
        ).replace(/^Última:/, 'Última leitura:'),
      ].join(' · ')}
      {...(!equipamento.ativo
        ? { marcadores: <EtiquetaDaOpcao etiqueta={ETIQUETA_DESATIVADA} tamanho="m" /> }
        : equipamento.status
          ? { marcadores: <BadgeStatus status={equipamento.status} /> }
          : {})}
      /* QUATRO dados-chave, como o exemplo da p.19: onde a maquina esta e para
         quem. Filial e data de atualizacao sao ficha, e foram para a aba
         Ficha — seis linhas aqui deixavam para as abas 184 px de 800. */
      dadosChave={
        <>
          <DadoChave rotulo="Cliente">{equipamento.cliente ?? 'Sem alocação'}</DadoChave>
          <DadoChave rotulo="Obra">{equipamento.obra ?? 'Sem alocação'}</DadoChave>
          <DadoChave rotulo="Centro de custo">
            {equipamento.centroCusto ?? 'Sem centro de custo'}
          </DadoChave>
          <DadoChave rotulo="Alocada desde">
            {equipamento.alocadoDesde ? formatarDataHora(equipamento.alocadoDesde) : 'Sem alocação'}
          </DadoChave>
        </>
      }
      abas={[
        {
          id: 'ficha',
          rotulo: 'Ficha',
          conteudo: editando ? (
            <FormularioFicha
              equipamento={equipamento}
              aoTerminar={() => {
                setEditando(false);
              }}
            />
          ) : (
            /* A FICHA NA JANELA LARGA (pedido do dono, 31/10/2026): o painel abre
               expandido, e a ficha usa a largura em vez de empilhar foto,
               medidor, dados e custos numa coluna so. Tres faixas, cada uma
               uma `grade-do-painel` (tres colunas expandido, uma no estreito):
               o uso da maquina com a foto na frente; os dados da ficha; e o
               custo, as pecas e a economia. "Editar ficha" fica no alto. O que a identidade ja diz (tipo, fabricante e modelo)
               nao se repete aqui. */
            <div className="flex flex-col gap-6">
              {/* EDITAR A FICHA (melhoria de 24/09) NO ALTO DA ABA, a vista ao
                  abrir a maquina, sem rolar ate os dados. So para quem pode
                  gerir o equipamento: botao que o servidor recusaria e promessa
                  que o perfil nao cumpre. Se a pessoa reduziu o painel, editar
                  o expande de novo (R01, p.29: formulario medio nao cabe em
                  360 px), sem gravar a escolha dela. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="txt-apoio text-tinta-secundaria">
                  {equipamento.atualizadoEm
                    ? `Ficha atualizada em ${formatarDataHora(equipamento.atualizadoEm)}`
                    : 'Ficha sem data de atualização'}
                </span>
                {podeEditar ? (
                  <Botao
                    nivel="secundario"
                    onClick={(evento) => {
                      evento.currentTarget.closest('[role="tabpanel"]')?.scrollTo({ top: 0 });
                      painel?.expandir();
                      setEditando(true);
                    }}
                  >
                    <Pencil aria-hidden="true" className="size-[var(--icone-controle)]" />
                    Editar ficha
                  </Botao>
                ) : null}
              </div>

              {/* A FOTO, no alto da ficha: e aqui que a pessoa a procura
                  (ADR-007). Enviar, trocar e remover so para quem pode gerir
                  o equipamento; ver e abrir, para todos. Ela e a primeira
                  celula da grade do uso, ao lado do medidor. */}
              {le.prontuario ? (
                <UsoNoProntuario
                  equipamentoId={equipamento.id}
                  tipoMedidor={equipamento.tipoMedidor}
                  prontuario={prontuario.data}
                  carregando={prontuario.isPending}
                  erro={prontuario.error}
                  primeiro={<FotoDoEquipamento equipamento={equipamento} podeEditar={podeEditar} />}
                />
              ) : (
                <div className="grade-do-painel">
                  <FotoDoEquipamento equipamento={equipamento} podeEditar={podeEditar} />
                </div>
              )}

              <section aria-labelledby={`${idDaFicha}-dados`} className="flex flex-col gap-4">
                <h3 id={`${idDaFicha}-dados`} className="txt-subtitulo border-t border-borda-sutil pt-4 text-tinta">
                  Dados da ficha
                </h3>

                <div className="grade-do-painel">
                  <SubtituloDaFicha>Identificação</SubtituloDaFicha>
                  <Linha
                    rotulo="Código interno"
                    valor={equipamento.codigoInterno ?? 'Não informado'}
                    apoio="Como a empresa chama a máquina no dia a dia. Não é o patrimônio."
                  />
                  <Linha rotulo="Patrimônio" valor={equipamento.patrimonio ?? 'Não informado'} />
                  <Linha rotulo="Número de série" valor={equipamento.numeroSerie ?? 'Não informado'} />
                  {/* A descricao so quando o titulo e o tipo: sem tipo, ela ja e
                      o titulo da identidade. */}
                  {equipamento.tipo ? <Linha rotulo="Descrição" valor={equipamento.descricao} /> : null}
                  <Linha
                    rotulo="Configuração"
                    valor={equipamento.configuracao ?? 'Sem configuração'}
                    apoio="A versão exata da máquina. É dela que a unidade herda o que não estiver preenchido aqui."
                  />
                  {/* O TIPO ja e o titulo da identidade; aqui fica DE ONDE ele
                      vem, que a identidade nao diz. */}
                  <Linha
                    rotulo="Origem do tipo"
                    valor={primeiraMaiuscula(origemEscrita(equipamento.origemDoTipo) ?? 'tipo não informado')}
                  />
                  <Linha rotulo="Filial" valor={equipamento.filial ?? 'Sem filial'} />

                  <SubtituloDaFicha>Especificação</SubtituloDaFicha>
                  {/* O MOTOR. Ele é o que a garantia e o plano de manutenção pedem
                      — sem ele, "peça compatível" vira consulta ao manual de papel.
                      O modelo pode vir da configuração; o número de série é sempre
                      desta máquina, e é ele que identifica a peça. */}
                  <Linha
                    rotulo="Motor"
                    valor={equipamento.motorModelo ?? 'Não informado'}
                    apoio={origemEscrita(equipamento.origemDoMotor)}
                  />
                  <Linha
                    rotulo="Série do motor"
                    valor={equipamento.motorNumeroSerie ?? 'Não informada'}
                    apoio="Identifica a peça, e não o modelo. Não se repete dentro da empresa."
                  />
                  <Linha
                    rotulo="Ano de fabricação"
                    valor={equipamento.anoFabricacao ? String(equipamento.anoFabricacao) : 'Não informado'}
                  />
                  <Linha
                    rotulo="Ano do modelo"
                    valor={equipamento.anoModelo ? String(equipamento.anoModelo) : 'Não informado'}
                  />
                  <Linha
                    rotulo="Ano de aquisição"
                    valor={equipamento.anoAquisicao ? String(equipamento.anoAquisicao) : 'Não informado'}
                  />
                  <Linha rotulo="Combustível" valor={rotuloDoCombustivel(equipamento.combustivel) ?? 'Não informado'} />
                  <Linha
                    rotulo="Capacidade do tanque"
                    valor={
                      equipamento.capacidadeTanqueL === null
                        ? 'Não informada'
                        : `${formatarInteiro(equipamento.capacidadeTanqueL)} L`
                    }
                    apoio={origemEscrita(equipamento.origemDaCapacidade)}
                  />
                  <Linha
                    rotulo="Medidor"
                    valor={medidor}
                    apoio={origemEscrita(equipamento.origemDoMedidor)}
                  />

                  <SubtituloDaFicha>Documento e abastecimento</SubtituloDaFicha>
                  <Linha rotulo="Placa" valor={equipamento.placa ?? 'Não informada'} />
                  <Linha rotulo="Renavam" valor={equipamento.renavam ?? 'Não informado'} />
                  {/* 0133 (decisao 59): so a maquina marcada entra na lista do posto parceiro. */}
                  <Linha
                    rotulo="Abastece em posto parceiro"
                    valor={equipamento.abasteceEmPosto ? 'Sim' : 'Não'}
                    apoio={
                      equipamento.abasteceEmPosto
                        ? 'Aparece na lista de máquinas do posto parceiro.'
                        : 'O posto parceiro não a vê. Marque na ficha se ela abastece fora dos tanques da empresa.'
                    }
                  />

                  <SubtituloDaFicha>Consumo</SubtituloDaFicha>
                  <Linha
                    rotulo="Consumo esperado, do manual"
                    valor={textoDaFaixaDoManual(equipamento)}
                    apoio={[
                      origemEscrita(equipamento.origemDaFaixa),
                      'É a faixa do manual da máquina, e é ela que confere o consumo de cada abastecimento.',
                    ]
                      .filter(Boolean)
                      .join('. ')}
                  />
                  {le.prontuario ? (
                    <ConsumoEfetivo
                      abastecimentos={prontuario.data?.abastecimentos}
                      carregando={prontuario.isPending}
                      semPermissao={ehSemPermissao(prontuario.error)}
                      unidade={equipamento.tipoMedidor === 'odometro' ? 'L/km' : 'L/h'}
                    />
                  ) : null}
                  {/* Ajustar a faixa e raro: o botao abre os campos (ver `FormularioFaixa`). */}
                  <div className="campo-linha">
                    <FormularioFaixa equipamento={equipamento} />
                  </div>
                </div>
              </section>

              {/* R06 (p.71): os dois blocos abaixo dos dados da ficha, nesta
                  ordem, e a economia da máquina (0107 · módulo 20) ao lado do
                  custo do mês. */}
              {le.prontuario ? <BlocosDeCustoEPecas prontuario={prontuario.data} carregando={prontuario.isPending} /> : null}
              <EconomiaDaMaquina equipamentoId={equipamento.id} />
            </div>
          ),
        },
        {
          id: 'alocacao',
          rotulo: 'Alocação',
          conteudo: (
            <div className="flex flex-col gap-6">
              {equipamento.ativo ? <FormularioAlocacao equipamento={equipamento} /> : <AvisoDaDesativada oque="alocação" />}
              {le.prontuario ? (
                <Historico
                  titulo="Histórico de alocações"
                  carregando={prontuario.isPending}
                  vazio="Esta máquina nunca foi alocada."
                  itens={(prontuario.data?.alocacoes ?? []).map((a) => ({
                    id: a.id,
                    principal: a.obra ?? 'Obra removida',
                    apoio: [a.cliente, a.centroCusto, a.contrato].filter(Boolean).join(' · '),
                    periodo: periodo(a.inicio, a.fim),
                  }))}
                />
              ) : null}
            </div>
          ),
        },
        {
          id: 'situacao',
          rotulo: 'Situação',
          conteudo: (
            <div className="flex flex-col gap-6">
              {equipamento.ativo ? <FormularioSituacao equipamento={equipamento} /> : <AvisoDaDesativada oque="situação" />}
              {le.prontuario ? (
                <Historico
                  titulo="Histórico de situações"
                  carregando={prontuario.isPending}
                  vazio="Esta máquina nunca teve situação registrada."
                  itens={(prontuario.data?.situacoes ?? []).map((s) => ({
                    id: s.id,
                    principal: s.status ?? 'Situação sem status registrado',
                    apoio: s.motivo,
                    periodo: periodo(s.inicio, s.fim),
                  }))}
                />
              ) : null}
            </div>
          ),
        },
        /* A aba Histórico é o prontuário: sem `prontuario.ler`, ela não existe para o perfil. */
        ...(le.prontuario
          ? [
              {
                id: 'historico',
                rotulo: 'Histórico',
                historia: 'ABA-EQP-09',
                conteudo: prontuario.isPending ? (
                  <EsqueletoDoHistorico oQue="Carregando a linha do tempo" />
                ) : prontuario.isError && ehSemPermissao(prontuario.error) ? (
                  /* O 403 é o quinto estado (p.34), e não o erro: diz o perfil a
                     pedir (prova do marco, 07/10/2026). */
                  <EstadoVazio
                    icone={ShieldCheck}
                    tom="atencao"
                    titulo="Você não tem acesso ao histórico da máquina"
                    descricao={fraseDoSemPermissao(prontuario.error, 'o prontuário da máquina')}
                  />
                ) : prontuario.isError ? (
                  <MensagemDeErro erro={prontuario.error} />
                ) : (prontuario.data?.historico.length ?? 0) === 0 ? (
                  <EstadoVazio
                    titulo="Nenhuma alteração desta máquina na trilha"
                    descricao="A trilha guarda quem mudou o quê e quando. Ela se preenche sozinha na primeira alteração desta máquina."
                  />
                ) : (
                  <ul className="flex flex-col gap-3">
                    {(prontuario.data?.historico ?? []).map((evento, indice) => (
                      <li
                        key={`${evento.recurso}-${indice}`}
                        className="flex flex-col gap-1 border-b border-borda-sutil pb-2"
                      >
                        <span className="flex items-baseline justify-between gap-4">
                          <span className="txt-corpo text-tinta">{descreverAcao(evento.acao, evento.recurso)}</span>
                          <span className="txt-apoio shrink-0 text-tinta-secundaria">
                            {evento.ocorridoEm ? formatarDataHora(evento.ocorridoEm) : '—'}
                          </span>
                        </span>
                        <span className="txt-apoio text-tinta-apagada">
                          {[evento.autor, evento.motivo].filter(Boolean).join(' · ') || 'Sem motivo declarado'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ),
              },
            ]
          : []),
      ].filter((aba) => abasVisiveis[aba.id as keyof typeof abasVisiveis])}
      /* Sem nota no rodape: a consequencia de desativar esta na confirmacao,
         que e onde ela e lida — e as duas linhas fixas de nota tiravam altura
         do corpo das abas, que e o que rola. */
      acoes={podeEditar && !equipamento.ativo ? [
        <Botao
          key="reativar"
          nivel="secundario"
          disabled={reativar.isPending}
          onClick={() => {
            reativar.mutate(equipamento.id);
          }}
        >
          {reativar.isPending ? 'Reativando' : 'Reativar equipamento'}
        </Botao>,
      ] : podeEditar ? [
        <BotaoDestrutivo
          key="desativar"
          rotulo="Desativar equipamento"
          pergunta={`Desativar o equipamento ${equipamento.codigo}?`}
          consequencia="Ele sai das listas de escolha de alocação e de abastecimento, inclusive a do posto parceiro, e a situação atual termina agora. As horas, os abastecimentos e a trilha já lançados continuam nos relatórios. Dá para reativar depois."
          rotuloConfirmar="Desativar equipamento"
          rotuloCancelar="Manter equipamento"
          aoConfirmar={() => {
            desativar.mutate(equipamento.id);
          }}
        />,
      ] : []}
      aoFechar={aoFechar}
    />
  );
}

/**
 * A MAQUINA DESATIVADA NAO RECEBE ALOCACAO NEM SITUACAO (31/10/2026): o
 * servidor recusa, e a aba diz isso antes, com o caminho de volta.
 */
function AvisoDaDesativada({ oque }: { readonly oque: string }) {
  return (
    <div className="bloco-rebaixado flex flex-col gap-1 p-4">
      <span className="txt-corpo text-tinta">Esta máquina está desativada.</span>
      <span className="txt-apoio text-tinta-secundaria">
        {`Ela não recebe ${oque} e fica fora das listas de escolha, inclusive a do posto parceiro. Para voltar a usá-la, clique em "Reativar equipamento" no alto do painel.`}
      </span>
    </div>
  );
}

/** O titulo de cada grupo da ficha, na linha inteira da grade. */
function SubtituloDaFicha({ children }: { readonly children: string }) {
  return <h4 className="campo-linha txt-rotulo text-tinta-secundaria">{children}</h4>;
}

function Linha({
  rotulo,
  valor,
  apoio,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly apoio?: string | undefined;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</span>
      <span className="txt-corpo text-pretty text-tinta [overflow-wrap:anywhere]">{valor}</span>
      {apoio ? <span className="txt-apoio text-pretty text-tinta-apagada">{apoio}</span> : null}
    </div>
  );
}

/**
 * O consumo EFETIVO, ao lado do esperado (decisão 20 do dono, 23/09): o
 * esperado é o do manual; o efetivo é o do uso real. A distância entre os dois
 * é o que o gestor quer ver — e cada um diz de onde veio.
 */
function ConsumoEfetivo({
  abastecimentos,
  carregando,
  unidade,
  semPermissao,
}: {
  readonly abastecimentos: Prontuario['abastecimentos'] | undefined;
  readonly carregando: boolean;
  readonly unidade: string;
  /** O prontuário foi recusado (403): o consumo não é "não carregou", é do perfil. */
  readonly semPermissao: boolean;
}) {
  if (!carregando && !abastecimentos && semPermissao) {
    return <Linha rotulo="Consumo efetivo, do uso real" valor="—" apoio="Seu perfil não vê o prontuário da máquina: o bloco do uso, acima, diz o perfil a pedir." />;
  }
  if (!carregando && !abastecimentos) {
    /* Falhou: sem esqueleto eterno. O motivo esta na aba Historico. */
    return <Linha rotulo="Consumo efetivo, do uso real" valor="—" apoio="Os abastecimentos não carregaram. A aba Histórico diz o motivo." />;
  }
  if (carregando || !abastecimentos) {
    /* Carregando (p.32): o rótulo já se sabe; o valor e a frase de apoio viram
       linhas, e não um "—" que se lê como "sem consumo". */
    return (
      <div className="flex flex-col gap-1">
        <span className="txt-legenda font-normal text-tinta-secundaria">Consumo efetivo, do uso real</span>
        <Carregando oQue="Carregando os abastecimentos" className="flex flex-col gap-1">
          <Barra className="my-1 h-3 w-24" />
          <Barra className="my-1 h-3 w-4/5" />
        </Carregando>
      </div>
    );
  }
  if (!abastecimentos.permitido) {
    return <Linha rotulo="Consumo efetivo, do uso real" valor="Sem acesso" apoio="O consumo sai dos abastecimentos, e o seu papel não vê combustível." />;
  }
  const efetivo = abastecimentos.consumoEfetivo;
  if (!efetivo) {
    return (
      <Linha
        rotulo="Consumo efetivo, do uso real"
        valor="Sem medição"
        apoio="Nenhum intervalo com leitura confiável nos últimos 90 dias. Ele aparece quando dois abastecimentos seguidos trazem o horímetro."
      />
    );
  }
  return (
    <Linha
      rotulo="Consumo efetivo, do uso real"
      valor={`${formatarDecimal(efetivo.valor, 1)} ${unidade}`}
      apoio={`Média dos últimos ${String(efetivo.dias)} dias, em ${formatarInteiro(efetivo.intervalos)} ${efetivo.intervalos === 1 ? 'intervalo' : 'intervalos'} entre leituras. Só combustível; ARLA fica de fora.`}
    />
  );
}

interface ItemDeHistorico {
  readonly id: string;
  readonly principal: string;
  readonly apoio: string;
  readonly periodo: string;
}

function Historico({
  titulo,
  itens,
  carregando,
  vazio,
}: {
  readonly titulo: string;
  readonly itens: readonly ItemDeHistorico[];
  readonly carregando: boolean;
  readonly vazio: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className="txt-rotulo text-tinta-secundaria">{titulo}</span>
      {carregando ? (
        <EsqueletoDoHistorico oQue={`Carregando o ${titulo.toLocaleLowerCase('pt-BR')}`} />
      ) : itens.length === 0 ? (
        <p className="txt-apoio text-tinta-apagada">{vazio}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {itens.map((item) => (
            <li key={item.id} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
              <span className="flex items-baseline justify-between gap-4">
                <span className="txt-corpo text-tinta">{item.principal}</span>
                <span className="txt-apoio shrink-0 text-tinta-secundaria">{item.periodo}</span>
              </span>
              {item.apoio ? <span className="txt-apoio text-tinta-apagada">{item.apoio}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Carregando (p.32) na forma dos itens do historico e da trilha: o que
 * aconteceu a esquerda, o periodo a direita e a linha de apoio embaixo, com a
 * mesma divisoria.
 */
function EsqueletoDoHistorico({ oQue }: { readonly oQue: string }) {
  return (
    <Carregando oQue={oQue} className="flex flex-col gap-3">
      {['w-1/2', 'w-2/5', 'w-3/5'].map((largura, i) => (
        <div key={largura} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
          <span className="flex items-center justify-between gap-4">
            <Barra className={`my-1 h-3 ${largura}`} />
            <Barra className="h-3 w-24" />
          </span>
          <Barra className={`my-1 h-3 ${i === 1 ? 'w-1/2' : 'w-3/4'}`} />
        </div>
      ))}
    </Carregando>
  );
}

/** "12/06/2025 — em aberto". O fim nulo e a vigencia corrente, e diz isso. */
function periodo(inicio: Date | null, fim: Date | null): string {
  const de = inicio ? formatarData(inicio) : '—';
  return fim ? `${de} a ${formatarData(fim)}` : `desde ${de}`;
}


/**
 * Os quatro indicadores saem da FROTA INTEIRA — nao da pagina, nao do filtro.
 *
 * E uma escolha, e o motivo esta em dois deles: "Frota" e "Operando agora" sao
 * clicaveis e APLICAM filtro. Indicador que seguisse o filtro que ele mesmo
 * aplica viraria auto-referencia — clicar em "Operando agora" faria o card
 * responder "de 5 equipamentos, 5 operando", que nao e resposta de pergunta
 * nenhuma. Por isso cada contexto NOMEIA a base.
 *
 * Nenhum card traz variacao: nao ha periodo de comparacao real ainda.
 */
function calcularIndicadores(equipamentos: readonly EquipamentoDaFrota[]) {
  const total = equipamentos.length;
  const operando = equipamentos.filter((e) => e.status === 'Operando').length;
  const paradas = equipamentos.filter((e) => e.status === 'Parada').length;
  const manutencao = equipamentos.filter((e) => e.status === 'Em manutenção').length;
  const semAlocacao = equipamentos.filter((e) => e.obra === null).length;

  return {
    total,
    operando,
    paradas,
    manutencao,
    foraDeOperacao: paradas + manutencao,
    semAlocacao,
  };
}
